import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { Button } from '../MUIs';
import BottomSheet from '../Ui/BottomSheet';
import Icon from '../../Assets/Icon/CustomIcon';
import { ArrowForwardIos as RightArrowIcon } from '@mui/icons-material';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import { BottomSheetActions } from '../../Reducer/BottomSheetReducer';
import { useDispatch } from 'react-redux';

export default function FilterBottomSheet({
  open,
  searchRef,
  onSearch, // 조회 함수
  depList = [],
  custList = [],
  onCustChange,
  tabConfig = null, // tabConfig가 있으면 동적으로 필터 옵션 표시/숨김
}) {
  const dispatch = useDispatch();
  const [mode, setMode] = useState('main'); // main / cust / dep / dateStart / dateEnd / year / quarter / month / yearRange
  // 기본값: 시작날짜 = 오늘 - 3년, 종료날짜 = 오늘
  const [startDate, setStartDate] = useState(dayjs().subtract(3, 'year'));
  const [endDate, setEndDate] = useState(dayjs());
  // 날짜 선택 모드 진입 시 원본 값 저장 (cancel 시 되돌리기 위해)
  const [originalStartDate, setOriginalStartDate] = useState(null);
  const [originalEndDate, setOriginalEndDate] = useState(null);
  const [selectedYear, setSelectedYear] = useState(
    new Date().getFullYear().toString()
  );
  const [selectedQuarter, setSelectedQuarter] = useState(
    Math.ceil((new Date().getMonth() + 1) / 3).toString()
  );
  const [selectedMonth, setSelectedMonth] = useState(
    (new Date().getMonth() + 1).toString()
  );
  // 년도 범위 필터
  const [startYear, setStartYear] = useState(
    (new Date().getFullYear() - 1).toString() // 작년
  );
  const [endYear, setEndYear] = useState(new Date().getFullYear().toString()); // 올해

  useEffect(() => {
    // open={false}가 아닐 때만 기본값 설정
    if (open === false) return;

    // 기본값 설정 (값이 없을 때만)
    if (!searchRef.current?.startDate) {
      const defaultStartDate = dayjs().subtract(3, 'year').format('YYYY-MM-DD');
      searchRef.current.startDate = defaultStartDate;
      setStartDate(dayjs(defaultStartDate));
    } else {
      setStartDate(dayjs(searchRef.current.startDate));
    }

    if (!searchRef.current?.endDate) {
      const defaultEndDate = dayjs().format('YYYY-MM-DD');
      searchRef.current.endDate = defaultEndDate;
      setEndDate(dayjs(defaultEndDate));
    } else {
      setEndDate(dayjs(searchRef.current.endDate));
    }

    if (!searchRef.current?.currentYear) {
      const defaultYear = new Date().getFullYear().toString();
      searchRef.current.currentYear = defaultYear;
      setSelectedYear(defaultYear);
    } else {
      setSelectedYear(searchRef.current.currentYear);
    }

    if (!searchRef.current?.quarter) {
      const defaultQuarter = Math.ceil(
        (new Date().getMonth() + 1) / 3
      ).toString();
      searchRef.current.quarter = defaultQuarter;
      setSelectedQuarter(defaultQuarter);
    } else {
      setSelectedQuarter(searchRef.current.quarter);
    }

    if (!searchRef.current?.month) {
      const defaultMonth = (new Date().getMonth() + 1).toString();
      searchRef.current.month = defaultMonth;
      setSelectedMonth(defaultMonth);
    } else {
      setSelectedMonth(searchRef.current.month);
    }

    // 년도 범위 필터 기본값 (작년 ~ 올해)
    if (!searchRef.current?.startYear) {
      const defaultStartYear = (new Date().getFullYear() - 1).toString(); // 작년
      searchRef.current.startYear = defaultStartYear;
      setStartYear(defaultStartYear);
    } else {
      setStartYear(searchRef.current.startYear);
    }

    if (!searchRef.current?.endYear) {
      const defaultEndYear = new Date().getFullYear().toString(); // 올해
      searchRef.current.endYear = defaultEndYear;
      setEndYear(defaultEndYear);
    } else {
      setEndYear(searchRef.current.endYear);
    }

    // 부서 기본값: 첫 번째 부서
    if (depList && depList.length > 0 && !searchRef.current?.depCd) {
      const firstDep = depList[0];
      searchRef.current.depCd = firstDep.value;
      searchRef.current.depName = firstDep.name;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (mode === 'dateStart') {
      setOriginalStartDate(startDate);
    } else if (mode === 'dateEnd') {
      setOriginalEndDate(endDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const HandleSelectDep = (dep) => {
    searchRef.current.depCd = dep.value;
    searchRef.current.depName = dep.name;
    setMode('main');
    // 부서 선택 시 즉시 적용
    if (onSearch) {
      onSearch();
    }
  };

  const HandleDateChange = (type, date) => {
    if (type === 'start') {
      setStartDate(date);
      searchRef.current.startDate = date.format('YYYY-MM-DD');
    } else {
      setEndDate(date);
      searchRef.current.endDate = date.format('YYYY-MM-DD');
    }
    // 날짜 변경 시 즉시 적용
    if (onSearch) {
      onSearch();
    }
  };

  const HandleDateCancel = (type) => {
    if (type === 'start' && originalStartDate !== null) {
      setStartDate(originalStartDate);
    } else if (type === 'end' && originalEndDate !== null) {
      setEndDate(originalEndDate);
    }
    setMode('main');
  };

  const HandleDateAccept = (type, date) => {
    HandleDateChange(type, date);
    setMode('main');
  };

  const HandleReset = () => {
    // 기본값으로 리셋
    const defaultStartDate = dayjs().subtract(3, 'year').format('YYYY-MM-DD');
    const defaultEndDate = dayjs().format('YYYY-MM-DD');
    const defaultYear = new Date().getFullYear().toString();
    const defaultQuarter = Math.ceil(
      (new Date().getMonth() + 1) / 3
    ).toString();
    const defaultMonth = (new Date().getMonth() + 1).toString();

    // 부서는 첫 번째 부서로 리셋
    if (depList && depList.length > 0) {
      const firstDep = depList[0];
      searchRef.current.depCd = firstDep.value;
      searchRef.current.depName = firstDep.name;
    } else {
      searchRef.current.depCd = '';
      searchRef.current.depName = '';
    }

    searchRef.current.startDate = defaultStartDate;
    searchRef.current.endDate = defaultEndDate;
    searchRef.current.currentYear = defaultYear;
    searchRef.current.quarter = defaultQuarter;
    searchRef.current.month = defaultMonth;

    setStartDate(dayjs(defaultStartDate));
    setEndDate(dayjs(defaultEndDate));
    setSelectedYear(defaultYear);
    setSelectedQuarter(defaultQuarter);
    setSelectedMonth(defaultMonth);
  };

  const HandleConfirm = () => {
    if (onSearch) {
      onSearch();
    }
    dispatch(BottomSheetActions.CloseBottomSheet());
  };

  // parameterMapping을 확인하여 표시할 필터 옵션 결정
  const GetVisibleFilters = () => {
    // M_DCM_BASIC_01.js에서 이미 DTL/MST 모드에 맞는 parameterMapping을 전달하므로
    // 여기서는 그냥 parameterMapping만 사용
    const paramMapping = tabConfig?.parameterMapping;

    if (!tabConfig || !paramMapping) {
      // tabConfig가 없으면 모든 필터 표시 (depList, year/quarter는 prop이 있을 때만)
      return {
        showCust: true,
        showDep: depList && depList.length > 0,
        showDate: true,
        showYear: false, // 기본적으로는 false, 필요시 prop으로 제어
        showQuarter: false, // 기본적으로는 false, 필요시 prop으로 제어
        showYearRange: false, // 년도 범위 필터
      };
    }

    const paramKeys = Object.keys(paramMapping);

    return {
      // 사업장: _CUST_CD 또는 _custCd가 있으면 표시
      showCust: paramKeys.includes('custCd'),
      // 부서: _DEP_CD 또는 _depCd가 있으면 표시
      showDep: paramKeys.includes('depCd') && depList && depList.length > 0,
      // 날짜: _START_DATE, _END_DATE 또는 _startDate, _endDate가 있으면 표시
      showDate:
        paramKeys.includes('startDate') && paramKeys.includes('endDate'),
      // 년도: _YEAR가 있으면 표시 (분기와 독립적으로)
      showYear: paramKeys.includes('year'),
      // 분기: _QUATER 또는 _quarter가 있으면 표시 (년도와 독립적으로)
      showQuarter: paramKeys.includes('quarter'),
      // 월
      showMonth: paramKeys.includes('month'),
      // 년도/월: _YEAR_MONTH가 있으면 표시
      showYearMonth: paramKeys.includes('yearMonth'),
      // 년도 범위: _START_YEAR, _END_YEAR가 있으면 표시
      showYearRange:
        paramKeys.includes('startYear') && paramKeys.includes('endYear'),
    };
  };

  const visibleFilters = GetVisibleFilters();

  // custList의 구조를 통일 (value/label 또는 custCd/custNm 모두 지원)
  const GetCustValue = (cust) => {
    return cust.value || cust.custCd || '';
  };

  const GetCustLabel = (cust) => {
    return cust.label || cust.custNm || '';
  };

  const IsCustSelected = (cust) => {
    const custValue = GetCustValue(cust);
    return searchRef?.current?.custCd === custValue;
  };

  const GetSelectedCustLabel = () => {
    const selectedCust = custList.find((cust) => IsCustSelected(cust));
    return selectedCust ? GetCustLabel(selectedCust) : '';
  };

  const HandleSelectCust = (cust) => {
    const custValue = GetCustValue(cust);
    const custLabel = GetCustLabel(cust);

    if (onCustChange) {
      // DCM 방식: cust 객체 전체 전달
      if (cust.value !== undefined || cust.custCd !== undefined) {
        onCustChange(cust);
      } else {
        // EIS 방식: custCd, custNm 개별 전달 (하위 호환성)
        onCustChange(custValue, custLabel);
      }
    }
    setMode('main');
    // 사업장 선택 시 즉시 적용
    if (onSearch) {
      onSearch();
    }
  };

  // depList 기준 선택 부서 라벨 (GetSelectedCustLabel과 동일 패턴)
  const GetDepValue = (dep) => dep?.value ?? '';
  const GetDepLabel = (dep) => dep?.name ?? dep?.label ?? '';
  const IsDepSelected = (dep) => searchRef?.current?.depCd === GetDepValue(dep);
  const GetSelectedDepLabel = () => {
    const selectedDep = depList.find((dep) => IsDepSelected(dep));
    return selectedDep ? GetDepLabel(selectedDep) : '';
  };

  return (
    <BottomSheet
      isHeaderChildren={true}
      headerText={
        mode === 'main'
          ? '필터'
          : mode === 'cust'
            ? '사업장 선택'
            : mode === 'dep'
              ? '부서 선택'
              : mode === 'date'
                ? '날짜 선택'
                : mode === 'year'
                  ? '년도 선택'
                  : mode === 'quarter'
                    ? '분기 선택'
                    : mode === 'month'
                      ? visibleFilters?.showYearMonth
                        ? '년도/월 선택'
                        : '월 선택'
                      : mode === 'yearRange'
                        ? '년도 범위 선택'
                        : ''
      }
      headerChildren={
        <>
          {mode === 'main' ? (
            <button
              style={{
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
              }}
              onClick={HandleReset}
            >
              <Icon.Refresh />
            </button>
          ) : mode === 'cust' ? (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <button
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  paddingRight: '0.3rem',
                }}
                onClick={() => setMode('main')}
              >
                <RightArrowIcon
                  style={{ transform: 'rotate(180deg)', width: '20px' }}
                />
              </button>
              <Typography
                sx={{
                  py: 2,
                  pr: 1,
                  color: '#3A3A3A',
                  fontSize: '18px',
                }}
              >
                사업장 선택
              </Typography>
            </Box>
          ) : mode === 'dep' ? (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <button
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  paddingRight: '0.3rem',
                }}
                onClick={() => setMode('main')}
              >
                <RightArrowIcon
                  style={{ transform: 'rotate(180deg)', width: '20px' }}
                />
              </button>
              <Typography
                sx={{
                  py: 2,
                  pr: 1,
                  color: '#3A3A3A',
                  fontSize: '18px',
                }}
              >
                부서 선택
              </Typography>
            </Box>
          ) : mode === 'dateStart' ? (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <button
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  paddingRight: '0.3rem',
                }}
                onClick={() => setMode('main')}
              >
                <RightArrowIcon
                  style={{ transform: 'rotate(180deg)', width: '20px' }}
                />
              </button>
              <Typography
                sx={{
                  py: 2,
                  pr: 1,
                  color: '#3A3A3A',
                  fontSize: '18px',
                }}
              >
                시작 날짜 선택
              </Typography>
            </Box>
          ) : mode === 'dateEnd' ? (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <button
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  paddingRight: '0.3rem',
                }}
                onClick={() => setMode('main')}
              >
                <RightArrowIcon
                  style={{ transform: 'rotate(180deg)', width: '20px' }}
                />
              </button>
              <Typography
                sx={{
                  py: 2,
                  pr: 1,
                  color: '#3A3A3A',
                  fontSize: '18px',
                }}
              >
                종료 날짜 선택
              </Typography>
            </Box>
          ) : mode === 'month' ? (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <button
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  paddingRight: '0.3rem',
                }}
                onClick={() => setMode('main')}
              >
                <RightArrowIcon
                  style={{ transform: 'rotate(180deg)', width: '20px' }}
                />
              </button>
              <Typography
                sx={{
                  py: 2,
                  pr: 1,
                  color: '#3A3A3A',
                  fontSize: '18px',
                }}
              >
                년도/월 선택
              </Typography>
            </Box>
          ) : mode === 'quarter' ? (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <button
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  paddingRight: '0.3rem',
                }}
                onClick={() => setMode('main')}
              >
                <RightArrowIcon
                  style={{ transform: 'rotate(180deg)', width: '20px' }}
                />
              </button>
              <Typography
                sx={{
                  py: 2,
                  pr: 1,
                  color: '#3A3A3A',
                  fontSize: '18px',
                }}
              >
                분기 선택
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <button
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  paddingRight: '0.3rem',
                }}
                onClick={() => setMode('main')}
              >
                <RightArrowIcon
                  style={{ transform: 'rotate(180deg)', width: '20px' }}
                />
              </button>
              <Typography
                sx={{
                  py: 2,
                  pr: 1,
                  color: '#3A3A3A',
                  fontSize: '18px',
                }}
              >
                년도 선택
              </Typography>
            </Box>
          )}
        </>
      }
    >
      <Box
        bgcolor="#fff"
        sx={{
          p: 1,
          pb: 2,
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        {/* 메인 화면 */}
        {mode === 'main' && (
          <>
            {visibleFilters.showCust && (
              <button
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
                onClick={() => setMode('cust')}
              >
                <Typography>사업장</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ color: '#888', fontSize: '14px' }}>
                    {GetSelectedCustLabel()}
                  </Typography>
                  <Icon.BottomArrow style={{ transform: 'rotate(-90deg)' }} />
                </Box>
              </button>
            )}

            {visibleFilters.showDep && (
              <button
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
                onClick={() => setMode('dep')}
              >
                <Typography>부서</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ color: '#888', fontSize: '14px' }}>
                    {GetSelectedDepLabel()}
                  </Typography>
                  <Icon.BottomArrow style={{ transform: 'rotate(-90deg)' }} />
                </Box>
              </button>
            )}

            {visibleFilters.showDate && (
              <>
                <button
                  style={{
                    backgroundColor: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                  onClick={() => setMode('dateStart')}
                >
                  <Typography>시작 날짜</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ color: '#888', fontSize: '14px' }}>
                      {searchRef.current?.startDate || ''}
                    </Typography>
                    <Icon.BottomArrow style={{ transform: 'rotate(-90deg)' }} />
                  </Box>
                </button>
                <button
                  style={{
                    backgroundColor: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                  onClick={() => setMode('dateEnd')}
                >
                  <Typography>종료 날짜</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ color: '#888', fontSize: '14px' }}>
                      {searchRef.current?.endDate || ''}
                    </Typography>
                    <Icon.BottomArrow style={{ transform: 'rotate(-90deg)' }} />
                  </Box>
                </button>
              </>
            )}

            {visibleFilters.showYear && (
              <button
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
                onClick={() => setMode('year')}
              >
                <Typography>년도</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ color: '#888', fontSize: '14px' }}>
                    {searchRef.current?.currentYear
                      ? `${searchRef.current.currentYear}년`
                      : ''}
                  </Typography>
                  <Icon.BottomArrow style={{ transform: 'rotate(-90deg)' }} />
                </Box>
              </button>
            )}

            {visibleFilters.showQuarter && (
              <button
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
                onClick={() => setMode('quarter')}
              >
                <Typography>분기</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ color: '#888', fontSize: '14px' }}>
                    {searchRef.current?.quarter
                      ? `${searchRef.current.quarter}분기`
                      : ''}
                  </Typography>
                  <Icon.BottomArrow style={{ transform: 'rotate(-90deg)' }} />
                </Box>
              </button>
            )}

            {visibleFilters.showMonth && (
              <button
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
                onClick={() => setMode('month')}
              >
                <Typography>월</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ color: '#888', fontSize: '14px' }}>
                    {searchRef.current?.month
                      ? `${searchRef.current.month}월`
                      : ''}
                  </Typography>
                  <Icon.BottomArrow style={{ transform: 'rotate(-90deg)' }} />
                </Box>
              </button>
            )}

            {visibleFilters.showYearMonth && (
              <button
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
                onClick={() => setMode('yearMonth')}
              >
                <Typography>년도/월</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ color: '#888', fontSize: '14px' }}>
                    {searchRef.current?.currentYear && searchRef.current?.month
                      ? `${searchRef.current.currentYear}년 ${searchRef.current.month}월`
                      : ''}
                  </Typography>
                  <Icon.BottomArrow style={{ transform: 'rotate(-90deg)' }} />
                </Box>
              </button>
            )}

            {visibleFilters.showYearRange && (
              <button
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
                onClick={() => setMode('yearRange')}
              >
                <Typography>년도 범위</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ color: '#888', fontSize: '14px' }}>
                    {searchRef.current?.startYear && searchRef.current?.endYear
                      ? `${searchRef.current.startYear}년 ~ ${searchRef.current.endYear}년`
                      : ''}
                  </Typography>
                  <Icon.BottomArrow style={{ transform: 'rotate(-90deg)' }} />
                </Box>
              </button>
            )}

            <Button
              name="확인"
              sx={{
                width: '100%',
                height: 36,
                borderRadius: '5px',
                backgroundColor: '#57B84D',
                color: '#fff',
              }}
              onClick={HandleConfirm}
            />
          </>
        )}

        {/* 사업장 리스트 */}
        {mode === 'cust' && (
          <Box style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {custList.length === 0 ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ py: 3, textAlign: 'center' }}
              >
                등록된 사업장이 없습니다.
              </Typography>
            ) : (
              custList.map((cust) => (
                <button
                  key={GetCustValue(cust)}
                  onClick={() => HandleSelectCust(cust)}
                  style={{
                    backgroundColor: IsCustSelected(cust)
                      ? '#E6EBFF'
                      : 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    textAlign: 'left',
                    padding: '4px 8px',
                    borderRadius: '5px',
                    fontSize: '16px',
                    fontWeight: 'bold',
                  }}
                >
                  {GetCustLabel(cust)}
                </button>
              ))
            )}
          </Box>
        )}

        {/* 부서 리스트 */}
        {mode === 'dep' && (
          <Box style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {depList.length === 0 ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ py: 3, textAlign: 'center' }}
              >
                등록된 부서가 없습니다.
              </Typography>
            ) : (
              depList.map((dep) => (
                <button
                  key={dep.value}
                  onClick={() => HandleSelectDep(dep)}
                  style={{
                    backgroundColor:
                      searchRef.current?.depCd === dep.value
                        ? '#E6EBFF'
                        : 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    textAlign: 'left',
                    padding: '4px 8px',
                    borderRadius: '5px',
                    fontSize: '16px',
                    fontWeight: 'bold',
                  }}
                >
                  {dep.name}
                </button>
              ))
            )}
          </Box>
        )}

        {/* 시작 날짜 선택 */}
        {mode === 'dateStart' && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ko">
              <StaticDatePicker
                value={startDate}
                onChange={(date) => setStartDate(date)}
                onAccept={(date) => HandleDateAccept('start', date)}
                slotProps={{
                  actionBar: {
                    actions: ['cancel', 'accept'],
                    onCancel: () => HandleDateCancel('start'),
                  },
                }}
              />
            </LocalizationProvider>
          </Box>
        )}

        {/* 종료 날짜 선택 */}
        {mode === 'dateEnd' && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ko">
              <StaticDatePicker
                value={endDate}
                onChange={(date) => setEndDate(date)}
                onAccept={(date) => HandleDateAccept('end', date)}
                minDate={startDate}
                slotProps={{
                  actionBar: {
                    actions: ['cancel', 'accept'],
                    onCancel: () => HandleDateCancel('end'),
                  },
                }}
              />
            </LocalizationProvider>
          </Box>
        )}

        {/* 년도만 선택 */}
        {mode === 'year' && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <Box>
              <Typography sx={{ mb: 1, fontWeight: 'bold' }}>년도</Typography>
              <input
                type="number"
                min="2000"
                max="2100"
                value={selectedYear}
                onChange={(e) => {
                  const year = e.target.value;
                  setSelectedYear(year);
                  searchRef.current.currentYear = year;
                  // 년도 변경 시 즉시 적용
                  if (onSearch) {
                    onSearch();
                  }
                }}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '5px',
                  fontSize: '16px',
                }}
              />
            </Box>
            <Button
              name="확인"
              sx={{
                width: '100%',
                height: 36,
                borderRadius: '5px',
                backgroundColor: '#57B84D',
                color: '#fff',
              }}
              onClick={() => {
                setMode('main');
              }}
            />
          </Box>
        )}

        {/* 분기 선택 */}
        {mode === 'quarter' && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <Box>
              <Typography sx={{ mb: 1, fontWeight: 'bold' }}>분기</Typography>
              <Box
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                }}
              >
                {['1', '2', '3', '4'].map((q) => (
                  <button
                    key={q}
                    onClick={() => {
                      setSelectedQuarter(q);
                      searchRef.current.quarter = q;
                      // 분기 선택 시 즉시 적용
                      if (onSearch) {
                        onSearch();
                      }
                    }}
                    style={{
                      backgroundColor:
                        selectedQuarter === q ? '#E6EBFF' : 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      textAlign: 'left',
                      padding: '4px 8px',
                      borderRadius: '5px',
                      fontSize: '16px',
                      fontWeight: 'bold',
                    }}
                  >
                    {q}분기
                  </button>
                ))}
              </Box>
            </Box>
            <Button
              name="확인"
              sx={{
                width: '100%',
                height: 36,
                borderRadius: '5px',
                backgroundColor: '#57B84D',
                color: '#fff',
              }}
              onClick={() => {
                setMode('main');
              }}
            />
          </Box>
        )}

        {/* 년도/월 선택 (_YEAR_MONTH인 경우) */}
        {mode === 'yearMonth' && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <Box>
              <Typography sx={{ mb: 1, fontWeight: 'bold' }}>년도</Typography>
              <input
                type="number"
                min="2000"
                max="2100"
                value={selectedYear}
                onChange={(e) => {
                  const year = e.target.value;
                  setSelectedYear(year);
                  searchRef.current.currentYear = year;
                  // 년도 변경 시 즉시 적용
                  if (onSearch) {
                    onSearch();
                  }
                }}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '5px',
                  fontSize: '16px',
                }}
              />
            </Box>
            <Box>
              <Typography sx={{ mb: 1, fontWeight: 'bold' }}>월</Typography>
              <Box
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                }}
              >
                {[
                  '1',
                  '2',
                  '3',
                  '4',
                  '5',
                  '6',
                  '7',
                  '8',
                  '9',
                  '10',
                  '11',
                  '12',
                ].map((m) => (
                  <button
                    key={m}
                    onClick={() => {
                      setSelectedMonth(m);
                      searchRef.current.month = m;
                      // 월 선택 시 즉시 적용
                      if (onSearch) {
                        onSearch();
                      }
                    }}
                    style={{
                      backgroundColor:
                        selectedMonth === m ? '#E6EBFF' : 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      textAlign: 'left',
                      padding: '4px 8px',
                      borderRadius: '5px',
                      fontSize: '16px',
                      fontWeight: 'bold',
                    }}
                  >
                    {m}월
                  </button>
                ))}
              </Box>
            </Box>
            <Button
              name="확인"
              sx={{
                width: '100%',
                height: 36,
                borderRadius: '5px',
                backgroundColor: '#57B84D',
                color: '#fff',
              }}
              onClick={() => {
                setMode('main');
              }}
            />
          </Box>
        )}

        {/* 월만 선택 (_MONTH인 경우) */}
        {mode === 'month' && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <Box>
              <Typography sx={{ mb: 1, fontWeight: 'bold' }}>월</Typography>
              <Box
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                }}
              >
                {[
                  '1',
                  '2',
                  '3',
                  '4',
                  '5',
                  '6',
                  '7',
                  '8',
                  '9',
                  '10',
                  '11',
                  '12',
                ].map((m) => (
                  <button
                    key={m}
                    onClick={() => {
                      setSelectedMonth(m);
                      searchRef.current.month = m;
                      // 월 선택 시 즉시 적용
                      if (onSearch) {
                        onSearch();
                      }
                    }}
                    style={{
                      backgroundColor:
                        selectedMonth === m ? '#E6EBFF' : 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      textAlign: 'left',
                      padding: '4px 8px',
                      borderRadius: '5px',
                      fontSize: '16px',
                      fontWeight: 'bold',
                    }}
                  >
                    {m}월
                  </button>
                ))}
              </Box>
            </Box>
            <Button
              name="확인"
              sx={{
                width: '100%',
                height: 36,
                borderRadius: '5px',
                backgroundColor: '#57B84D',
                color: '#fff',
              }}
              onClick={() => {
                setMode('main');
              }}
            />
          </Box>
        )}

        {/* 년도 범위 선택 */}
        {mode === 'yearRange' && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <Box>
              <Typography sx={{ mb: 1, fontWeight: 'bold' }}>
                시작년도
              </Typography>
              <input
                type="number"
                min="2000"
                max="2100"
                value={startYear}
                onChange={(e) => {
                  const year = e.target.value;
                  setStartYear(year);
                  searchRef.current.startYear = year;
                  // 년도 변경 시 즉시 적용
                  if (onSearch) {
                    onSearch();
                  }
                }}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '5px',
                  fontSize: '16px',
                }}
              />
            </Box>
            <Box>
              <Typography sx={{ mb: 1, fontWeight: 'bold' }}>
                종료년도
              </Typography>
              <input
                type="number"
                min="2000"
                max="2100"
                value={endYear}
                onChange={(e) => {
                  const year = e.target.value;
                  setEndYear(year);
                  searchRef.current.endYear = year;
                  // 년도 변경 시 즉시 적용
                  if (onSearch) {
                    onSearch();
                  }
                }}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '5px',
                  fontSize: '16px',
                }}
              />
            </Box>
            <Button
              name="확인"
              sx={{
                width: '100%',
                height: 36,
                borderRadius: '5px',
                backgroundColor: '#57B84D',
                color: '#fff',
              }}
              onClick={() => {
                setMode('main');
              }}
            />
          </Box>
        )}
      </Box>
    </BottomSheet>
  );
}
